import { useEffect, useRef } from "react";
import api from "@/lib/api";

// Site geneli ANLIK yenileme: tek bir SSE akışı (/events) sunucudan "değişiklik var" sinyalini anında iter;
// SSE açık görünse de proxy tamponlayıp olay iletmeyebilir → 3 sn'lik hafif sürüm yoklaması HER ZAMAN çalışır (en geç ~3 sn).
// Sürüm değişince tüm abone sayfalar veriyi yeniden çeker.
// Yazma koruması: kullanıcı bir alana yazıyorken yenileme ertelenir (form ezilmesi / kasma olmaz).
const POLL_MS = 3000;
const TYPING_IDLE_MS = 1500;
const subs = new Set();
let version = null;
let timer = null;
let cartTimer = null;
let inflight = false;
let es = { ctrl: null, retry: 0, failures: 0 };
let lastInputAt = 0;
let pendingRun = null;

const isTyping = () => {
  const el = document.activeElement;
  const editable = el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);
  return editable && Date.now() - lastInputAt < TYPING_IDLE_MS;
};
const runAll = () => {
  if (isTyping()) {
    if (!pendingRun) pendingRun = setTimeout(() => { pendingRun = null; runAll(); }, TYPING_IDLE_MS);
    return;
  }
  subs.forEach((s) => { if (!s.cart) { try { s.current && s.current(); } catch { /* noop */ } } });
};
// Canlı sepet kanalı: yazma korumasız — başka kullanıcının sepet değişikliği yazarken de anında gelir
const runCart = () => subs.forEach((s) => { if (s.cart) { try { s.current && s.current(); } catch { /* noop */ } } });
let cartVersion = null;
const applyVersion = (v, c) => {
  if (version !== null && v !== version) runAll();
  version = v;
  if (c != null) {
    if (cartVersion !== null && c !== cartVersion) runCart();
    cartVersion = c;
  }
};

const tick = async () => {
  if (document.visibilityState !== "visible" || inflight || !localStorage.getItem("ge_token")) return;
  inflight = true;
  try {
    const { data } = await api.get("/changes");
    applyVersion(Number(data?.v ?? 0), data?.c != null ? Number(data.c) : null);
  } catch { /* ağ hatası — bir sonraki turda tekrar */ }
  finally { inflight = false; }
};

// fetch tabanlı SSE (EventSource Authorization başlığı taşıyamaz)
const openStream = async () => {
  const token = localStorage.getItem("ge_token");
  if (!token || es.ctrl || subs.size === 0) return;
  const ctrl = new AbortController();
  es.ctrl = ctrl;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`, { headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" }, signal: ctrl.signal, cache: "no-store" });
    if (!res.ok || !res.body) throw new Error(`sse ${res.status}`);
    es.failures = 0;
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
        const data = chunk.split("\n").find((l) => l.startsWith("data:"));
        if (data) { try { const d = JSON.parse(data.slice(5)); applyVersion(Number(d.v ?? 0), d.c != null ? Number(d.c) : null); } catch { /* noop */ } }
      }
    }
  } catch { es.failures += 1; }
  finally {
    es.ctrl = null;
    if (subs.size > 0 && document.visibilityState === "visible") {
      const wait = Math.min(30000, 1000 * 2 ** Math.min(es.failures, 5));
      es.retry = setTimeout(openStream, wait);
    }
  }
};
const closeStream = () => { if (es.retry) { clearTimeout(es.retry); es.retry = 0; } if (es.ctrl) { try { es.ctrl.abort(); } catch { /* noop */ } es.ctrl = null; } };

const hasCartSub = () => { for (const s of subs) if (s.cart) return true; return false; };
const ensurePoller = () => {
  if (!timer) timer = setInterval(tick, POLL_MS);
  // Canlı sepet ekranı AÇIKKEN güvenilir hızlı yoklama: SSE proxy tarafından tamponlansa/gecikse bile
  // başka kullanıcının eklediği ürün / açtığı sepet en geç ~0.7 sn'de görünür (30 sn'lik yavaş yenilemeyi beklemez).
  if (!cartTimer) cartTimer = setInterval(() => { if (hasCartSub() && document.visibilityState === "visible") tick(); }, 700);
  openStream();
};
const maybeStop = () => { if (subs.size === 0) { if (timer) { clearInterval(timer); timer = null; } if (cartTimer) { clearInterval(cartTimer); cartTimer = null; } closeStream(); } };

if (typeof document !== "undefined") {
  document.addEventListener("input", () => { lastInputAt = Date.now(); }, true);
  document.addEventListener("keydown", () => { lastInputAt = Date.now(); }, true);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") openStream(); else closeStream(); });
}

export function useAutoRefresh(callback, { interval = 5000, enabled = true, cart = false } = {}) {
  const cbRef = useRef(callback);
  cbRef.cart = cart;
  useEffect(() => {
    cbRef.current = callback;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    subs.add(cbRef);
    ensurePoller();
    const run = () => {
      if (document.visibilityState === "visible" && (cart || !isTyping())) {
        try { cbRef.current && cbRef.current(); } catch { /* noop */ }
      }
    };
    const slow = setInterval(run, Math.max(interval * 4, 30000));
    const onVis = () => { if (document.visibilityState === "visible") { run(); tick(); } };
    const onFocus = () => run();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      subs.delete(cbRef);
      maybeStop();
      clearInterval(slow);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, [interval, enabled, cart]);
}

// Canlı sepet aboneliği: sepet sayacı (c) değişince anında çalışır, yazma koruması uygulanmaz
export function useCartChanges(callback) {
  return useAutoRefresh(callback, { interval: 10000, cart: true });
}
